/**
 * KYC Personal Info Mock Data Generator
 *
 * 该模块提供 KYC 个人信息表单的模拟数据生成功能
 * 使用 @faker-js/faker 生成符合表单验证规则的测试数据
 *
 * @module mockDataGenerator
 * @description 独立的模拟数据生成器，可轻松迁移至独立的 npm 包
 */

import { faker } from "@faker-js/faker";

/* ============================================================================
 * 类型定义
 * ========================================================================== */

/**
 * 国家数据类型
 */
interface CountryData {
  code2: string;
  cn: string;
  en: string;
  hk: string;
  tel: string;
  url: string;
  priority: number;
}

/**
 * 国家JSON数据结构
 */
interface CountryJsonData {
  code: string;
  msg: string;
  data: CountryData[];
}

// 导入真实的国家数据
import countryAllDataJson from "../../data/country.json";
import countryAllowedDataJson from "../../data/country-allowed.json";
import countryRestrictedDataJson from "../../data/country-restricted.json";

const countryAllData = countryAllDataJson as CountryJsonData;
const countryAllowedData = countryAllowedDataJson as CountryJsonData;
const countryRestrictedData = countryRestrictedDataJson as CountryJsonData;

/**
 * 证件有效期类型
 */
export interface DocumentValidityDate {
  /** 类型：永久有效或选择日期 */
  type: "permanent" | "date";
  /** 有效期日期（仅当type为'date'时有效） */
  idExpiryDate?: string;
}

/**
 * 个人信息表单数据类型
 */
export interface PersonalInfoFormData {
  /** 国籍/公民权（ISO 3166-1 alpha-3 代码） */
  nationality: string;
  /** 证件类型 */
  idType: string;
  /** 英文名和中间名 */
  firstName: string;
  /** 英文姓 */
  lastName: string;
  /** 中文姓名（可选） */
  chineseName?: string;
  /** 证件号码 */
  idNumber: string;
  /** 出生日期（ISO 8601 格式：YYYY-MM-DD） */
  birthday: string;
  /** 出生地（ISO 3166-1 alpha-3 代码） */
  birthPlace: string;
  /** 性别 */
  gender: "MALE" | "FEMALE";
  /** 证件有效期 */
  idExpiryDate: DocumentValidityDate;
  /** 是否为香港证监会相关雇员或董事 */
  isSfcStaff: "YES" | "NO";
  /** 亲属是否为 Finex 或其关联公司雇员或董事 */
  isBitcoinWorldStaff: "YES" | "NO";
  /** 海外居民信息（条件性字段） */
  overseasResident?: OverseasResidentInfo;
}

/**
 * 海外居民身份信息
 */
export interface OverseasResidentInfo {
  /** 海外居留证件签发国家/地区 */
  idIssuingCountry: string;
  /** 海外居留证件类型 */
  idTypeOverseas: string;
  /** 海外居留证件有效期 */
  idExpiryDateOverseas: DocumentValidityDate;
  /** 海外居留证件照正面（文件ID数组） */
  idFrontOverseas: string[];
  /** 海外居留证件照背面（文件ID数组） */
  idBackOverseas: string[];
  /** 是否为居住在海外的中国公民 */
  isChineseCitizen: "YES" | "NO";
}

/**
 * Mock 数据生成配置选项
 */
export interface MockDataGeneratorOptions {
  /** 是否包含海外居民信息 */
  includeOverseas?: boolean;
  /** 指定国籍（默认随机） */
  nationality?: string;
  /** 是否包含中文姓名（默认根据国籍判断） */
  includeChineseName?: boolean;
  /** 证件是否为长期有效（默认随机） */
  isPermanentId?: boolean;
  /** 自定义种子，用于生成可重复的数据 */
  seed?: number;
}

/* ============================================================================
 * 常量配置
 * ========================================================================== */

/**
 * 所有国家列表（来自 country.json）
 * 用于 nationality 和 birthPlace 字段
 */
const ALL_COUNTRIES = countryAllData.data;

/**
 * 允许的国家列表（来自 country-allowed.json）
 */
const ALLOWED_COUNTRIES = countryAllowedData.data;

/**
 * 受限制的国家列表（来自 country-restricted.json）
 */
const RESTRICTED_COUNTRIES = countryRestrictedData.data;

/**
 * 国籍/地区代码列表（ISO 3166-1 alpha-3）
 * 使用真实的国家数据
 */
const NATIONALITY_CODES = {
  /** 所有国家（用于birthPlace） */
  ALL: ALL_COUNTRIES.map((c) => c.code2),
  /** 允许的国家/地区 */
  ALLOWED: ALLOWED_COUNTRIES.map((c) => c.code2),
  /** 受限制的国家/地区 */
  RESTRICTED: RESTRICTED_COUNTRIES.map((c) => c.code2),
  /** 允许和受限制国家的合集（用于nationality） */
  ALLOWED_AND_RESTRICTED: [
    ...ALLOWED_COUNTRIES.map((c) => c.code2),
    ...RESTRICTED_COUNTRIES.map((c) => c.code2),
  ],
} as const;

/**
 * 证件类型
 */
const ID_TYPES = {
  /** 护照 */
  PASSPORT: "PASSPORT",
  /** 身份证（仅限香港） */
  ID_CARD: "ID_CARD",
  /** 临时身份证（海外居留） */
  TEMPORARY_ID_CARD: "TEMPORARY_ID_CARD",
} as const;

/**
 * 性别选项
 */
const GENDER_OPTIONS = ["MALE", "FEMALE"] as const;

/**
 * 是/否选项
 */
const YES_NO_OPTIONS = ["YES", "NO"] as const;

/**
 * 常用中文姓
 */
const CHINESE_SURNAMES = [
  "王",
  "李",
  "张",
  "刘",
  "陈",
  "杨",
  "黄",
  "赵",
  "周",
  "吴",
  "徐",
  "孙",
  "马",
  "朱",
  "胡",
  "郭",
  "何",
  "林",
  "罗",
  "高",
  "郑",
  "梁",
  "谢",
  "宋",
  "唐",
  "许",
  "韩",
  "冯",
  "邓",
  "曹",
] as const;

/**
 * 常用中文名字
 */
const CHINESE_GIVEN_NAMES_1 = [
  "伟",
  "芳",
  "娜",
  "秀英",
  "敏",
  "静",
  "丽",
  "强",
  "磊",
  "军",
  "洋",
  "勇",
  "艳",
  "杰",
  "娟",
  "涛",
  "明",
  "超",
  "秀兰",
  "霞",
] as const;

const CHINESE_GIVEN_NAMES_2 = [
  "华",
  "文",
  "玲",
  "萍",
  "红",
  "峰",
  "鹏",
  "辉",
  "君",
  "宇",
  "建",
  "国",
  "英",
  "云",
  "波",
  "涛",
  "凯",
  "欣",
  "琳",
  "婷",
] as const;

/* ============================================================================
 * 辅助函数
 * ========================================================================== */

/**
 * 生成符合规则的英文名字（允许字母、数字和空格，必须包含字母，不以空格开头）
 *
 * @param fakerInstance - faker 实例
 * @returns 英文名字
 */
function generateEnglishName(fakerInstance: typeof faker): string {
  // 80% 概率使用普通名字，20% 概率使用复合名字（带空格）
  if (fakerInstance.number.int({ min: 1, max: 10 }) <= 8) {
    return fakerInstance.person.firstName();
  }

  // 生成复合名字
  const firstName = fakerInstance.person.firstName();
  const middleName = fakerInstance.person.firstName();
  return `${firstName} ${middleName}`;
}

/**
 * 生成符合规则的英文姓氏
 *
 * @param fakerInstance - faker 实例
 * @returns 英文姓氏
 */
function generateEnglishSurname(fakerInstance: typeof faker): string {
  return fakerInstance.person.lastName();
}

/**
 * 生成符合规则的中文姓名（允许中文、数字和空格，不以空格开头）
 *
 * @param fakerInstance - faker 实例
 * @returns 中文姓名
 */
function generateChineseName(fakerInstance: typeof faker): string {
  const surname = fakerInstance.helpers.arrayElement([...CHINESE_SURNAMES]);

  // 80% 概率是双字名，20% 概率是单字名
  if (fakerInstance.number.int({ min: 1, max: 10 }) <= 8) {
    const givenName1 = fakerInstance.helpers.arrayElement([
      ...CHINESE_GIVEN_NAMES_1,
    ]);
    const givenName2 = fakerInstance.helpers.arrayElement([
      ...CHINESE_GIVEN_NAMES_2,
    ]);
    return surname + givenName1 + givenName2;
  } else {
    const givenName = fakerInstance.helpers.arrayElement([
      ...CHINESE_GIVEN_NAMES_1,
    ]);
    return surname + givenName;
  }
}

/**
 * 生成证件号码（允许字母、数字和特殊字符）
 *
 * @param fakerInstance - faker 实例
 * @param idType - 证件类型
 * @param nationality - 国籍
 * @returns 证件号码
 */
function generateIdNumber(
  fakerInstance: typeof faker,
  idType: string,
  nationality: string
): string {
  if (idType === ID_TYPES.PASSPORT) {
    // 护照号码格式：通常是 1-2 个字母 + 7-9 个数字
    const prefix = fakerInstance.string.alpha({
      length: fakerInstance.number.int({ min: 1, max: 2 }),
      casing: "upper",
    });
    const numbers = fakerInstance.string.numeric(
      fakerInstance.number.int({ min: 7, max: 9 })
    );
    return prefix + numbers;
  }

  if (idType === ID_TYPES.ID_CARD && nationality === "HKG") {
    // 香港身份证格式：字母 + 6 个数字 + 括号 + 校验码
    const letter = fakerInstance.string.alpha({ length: 1, casing: "upper" });
    const numbers = fakerInstance.string.numeric(6);
    const checkDigit = fakerInstance.helpers.arrayElement([
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "A",
    ]);
    return `${letter}${numbers}(${checkDigit})`;
  }

  // 默认：字母数字混合
  const prefix = fakerInstance.string.alpha({ length: 2, casing: "upper" });
  const numbers = fakerInstance.string.numeric(8);
  return prefix + numbers;
}

/**
 * 生成出生日期（18-100 岁之间）
 *
 * @param fakerInstance - faker 实例
 * @returns ISO 8601 格式的日期字符串
 */
function generateBirthday(fakerInstance: typeof faker): string {
  // 生成 18-100 岁的出生日期
  const birthDate = fakerInstance.date.birthdate({
    min: 18,
    max: 100,
    mode: "age",
  });

  const year = birthDate.getFullYear();
  const month = String(birthDate.getMonth() + 1).padStart(2, "0");
  const day = String(birthDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * 生成证件有效期
 *
 * @param fakerInstance - faker 实例
 * @param isPermanent - 是否为长期有效
 * @returns 证件有效期对象
 */
function generateExpiryDate(
  fakerInstance: typeof faker,
  isPermanent?: boolean
): DocumentValidityDate {
  // 如果未指定，随机决定是否为长期有效（20% 概率）
  if (isPermanent === undefined) {
    isPermanent = fakerInstance.number.int({ min: 1, max: 10 }) <= 2;
  }

  if (isPermanent) {
    return {
      type: "permanent",
      idExpiryDate: undefined,
    };
  }

  // 生成 1-10 年后的日期
  const expiryDate = fakerInstance.date.future({
    years: fakerInstance.number.int({ min: 1, max: 10 }),
  });

  const year = expiryDate.getFullYear();
  const month = String(expiryDate.getMonth() + 1).padStart(2, "0");
  const day = String(expiryDate.getDate()).padStart(2, "0");

  return {
    type: "date",
    idExpiryDate: `${year}-${month}-${day}`,
  };
}

/**
 * 生成海外居民信息
 *
 * @param fakerInstance - faker 实例
 * @param nationality - 原国籍
 * @returns 海外居民信息对象
 */
function generateOverseasResidentInfo(
  fakerInstance: typeof faker,
  nationality: string
): OverseasResidentInfo {
  // 签发国家应该与原国籍不同，从允许的国家中选择
  const issuingCountries = NATIONALITY_CODES.ALLOWED.filter(
    (code) => code !== nationality
  );
  const idIssuingCountry = fakerInstance.helpers.arrayElement(issuingCountries);

  return {
    idIssuingCountry,
    idTypeOverseas: ID_TYPES.TEMPORARY_ID_CARD,
    idExpiryDateOverseas: generateExpiryDate(fakerInstance, false), // 临时证件通常不是长期有效
    idFrontOverseas: [],
    idBackOverseas: [],
    isChineseCitizen: "YES",
  };
}

/* ============================================================================
 * 主函数
 * ========================================================================== */

/**
 * 生成个人信息表单模拟数据
 *
 * @param options - 生成配置选项
 * @returns 符合表单验证规则的模拟数据
 *
 * @example
 * ```typescript
 * // 生成基本个人信息
 * const basicData = generatePersonalInfoMockData();
 *
 * // 生成包含海外居民信息的数据
 * const overseasData = generatePersonalInfoMockData({
 *   includeOverseas: true,
 *   nationality: 'CHN'
 * });
 *
 * // 生成可重复的数据（用于测试）
 * const repeatableData = generatePersonalInfoMockData({
 *   seed: 12345
 * });
 * ```
 */
export function generatePersonalInfoMockData(
  options: MockDataGeneratorOptions = {}
): PersonalInfoFormData {
  const {
    includeOverseas = false,
    nationality: specifiedNationality,
    includeChineseName: specifiedIncludeChineseName,
    isPermanentId,
    seed,
  } = options;

  // 创建 faker 实例（如果提供种子，使用种子）
  const fakerInstance = seed !== undefined ? faker : faker;
  if (seed !== undefined) {
    fakerInstance.seed(seed);
  }

  // 确定国籍（从允许和受限制国家列表中随机选择）
  const nationality =
    specifiedNationality ||
    fakerInstance.helpers.arrayElement(
      NATIONALITY_CODES.ALLOWED_AND_RESTRICTED
    );

  // 根据国籍确定可用的证件类型
  const availableIdTypes =
    nationality === "HKG"
      ? [ID_TYPES.PASSPORT, ID_TYPES.ID_CARD]
      : [ID_TYPES.PASSPORT];
  const idType = fakerInstance.helpers.arrayElement(availableIdTypes);

  // 确定是否包含中文姓名（默认：中国大陆和香港有更高概率）
  const shouldIncludeChineseName =
    specifiedIncludeChineseName !== undefined
      ? specifiedIncludeChineseName
      : ["CHN", "HKG"].includes(nationality)
        ? fakerInstance.number.int({ min: 1, max: 10 }) <= 8 // 80% 概率
        : fakerInstance.number.int({ min: 1, max: 10 }) <= 1; // 10% 概率

  // 基础数据
  let mockData: PersonalInfoFormData = {
    nationality,
    idType,
    firstName: generateEnglishName(fakerInstance),
    lastName: generateEnglishSurname(fakerInstance),
    chineseName: shouldIncludeChineseName
      ? generateChineseName(fakerInstance)
      : undefined,
    idNumber: generateIdNumber(fakerInstance, idType, nationality),
    birthday: generateBirthday(fakerInstance),
    birthPlace: fakerInstance.helpers.arrayElement(NATIONALITY_CODES.ALL),
    gender: fakerInstance.helpers.arrayElement([...GENDER_OPTIONS]),
    idExpiryDate: generateExpiryDate(fakerInstance, isPermanentId),
    isSfcStaff: fakerInstance.helpers.arrayElement([...YES_NO_OPTIONS]),
    isBitcoinWorldStaff: fakerInstance.helpers.arrayElement([
      ...YES_NO_OPTIONS,
    ]),
  };

  // 如果需要海外居民信息
  if (includeOverseas) {
    mockData = {
      ...mockData,
      ...generateOverseasResidentInfo(fakerInstance, nationality),
    };
  }

  return mockData;
}

/**
 * 批量生成个人信息表单模拟数据
 *
 * @param count - 生成数据的数量
 * @param options - 生成配置选项
 * @returns 模拟数据数组
 *
 * @example
 * ```typescript
 * // 生成 10 条测试数据
 * const mockDataList = generatePersonalInfoMockDataBatch(10);
 *
 * // 生成 5 条包含海外信息的数据
 * const overseasDataList = generatePersonalInfoMockDataBatch(5, {
 *   includeOverseas: true
 * });
 *
 * // 生成可重复的批量数据（每条数据使用递增的种子）
 * const repeatableDataList = generatePersonalInfoMockDataBatch(5, {
 *   seed: 1000  // 将生成种子 1000, 1001, 1002, 1003, 1004
 * });
 * ```
 */
export function generatePersonalInfoMockDataBatch(
  count: number,
  options: MockDataGeneratorOptions = {}
): PersonalInfoFormData[] {
  return Array.from({ length: count }, (_, index) => {
    // 如果提供了种子，为每条数据使用递增的种子
    const itemOptions =
      options.seed !== undefined
        ? { ...options, seed: options.seed + index }
        : options;

    return generatePersonalInfoMockData(itemOptions);
  });
}

/**
 * 生成特定场景的模拟数据
 *
 * 提供一些预设场景，方便快速生成特定类型的测试数据
 */
export const mockDataPresets = {
  /**
   * 香港居民（身份证）
   */
  hkResidentWithIdCard: (): PersonalInfoFormData => {
    const data = generatePersonalInfoMockData({
      nationality: "HKG",
      includeChineseName: true,
    });
    // 确保是身份证（如果随机到护照，重新生成）
    if (data.idType !== ID_TYPES.ID_CARD) {
      data.idType = ID_TYPES.ID_CARD;
      data.idNumber = generateIdNumber(faker, ID_TYPES.ID_CARD, "HKG");
    }
    return data;
  },

  /**
   * 中国大陆居民（护照）
   */
  mainlandChineseWithPassport: (): PersonalInfoFormData =>
    generatePersonalInfoMockData({
      nationality: "CHN",
      includeChineseName: true,
    }),

  /**
   * 海外居住的中国公民
   */
  overseasChinese: (): PersonalInfoFormData =>
    generatePersonalInfoMockData({
      nationality: "CHN",
      includeChineseName: true,
      includeOverseas: true,
    }),

  /**
   * 外国居民（护照）
   */
  foreignResident: (): PersonalInfoFormData =>
    generatePersonalInfoMockData({
      nationality: faker.helpers.arrayElement([
        "USA",
        "GBR",
        "JPN",
        "KOR",
        "SGP",
      ]),
      includeChineseName: false,
    }),

  /**
   * 长期有效证件
   */
  permanentIdDocument: (): PersonalInfoFormData =>
    generatePersonalInfoMockData({
      isPermanentId: true,
    }),
};

/* ============================================================================
 * 导出
 * ========================================================================== */

export default {
  generatePersonalInfoMockData,
  generatePersonalInfoMockDataBatch,
  mockDataPresets,
};
